/**
 * Sold detail — dedicated SEO page for a closed deal.
 * Route: /sold/:slug
 *
 * Anonymized: price band + buyer channel + days-to-close. Optional
 * admin-authored narrative (`sold_narrative_md`) renders as prose.
 * Falls back to a generated blurb when the broker hasn't written one.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { renderParagraphs } from "@/lib/md";
import SiteHeader from "@/components/marketplace/SiteHeader";
import SiteFooter from "@/components/marketplace/SiteFooter";
import BullseyeEmbed from "@/components/marketplace/BullseyeEmbed";
import Seo from "@/components/Seo";

export default function SoldDetail() {
  const { slug } = useParams();
  const [row, setRow] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | 404

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    api.get(`/listings/sold/${slug}`)
      .then((r) => { if (!cancelled) { setRow(r.data); setState("ready"); } })
      .catch(() => { if (!cancelled) setState("404"); });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => { return () => { document.title = "CasperDomains"; }; }, []);

  if (state === "loading") {
    return (
      <>
        <SiteHeader />
        <div style={{ padding: "120px 24px", textAlign: "center", color: "var(--cd-gray-500)" }}>
          Loading case study…
        </div>
        <SiteFooter />
      </>
    );
  }

  if (state === "404") {
    return (
      <>
        <SiteHeader />
        <div style={{ padding: "120px 24px", textAlign: "center" }} data-testid="sold-404">
          <h1 className="cd-h2" style={{ marginBottom: 12 }}>Case study not found</h1>
          <p style={{ color: "var(--cd-gray-500)", marginBottom: 24 }}>
            This domain hasn't been marked sold yet, or the link is wrong.
          </p>
          <Link to="/" className="cd-btn cd-btn--outline">Back to marketplace</Link>
        </div>
        <SiteFooter />
      </>
    );
  }

  // ready
  const soldDate = row.sold_at ? new Date(row.sold_at) : null;
  const soldPretty = soldDate
    ? soldDate.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : "—";
  const narrative = (row.sold_narrative_md || "").trim();
  const fallback = defaultNarrative(row);

  return (
    <>
      <Seo
        title={`${row.domain} · sold · CasperDomains`}
        description={`Case study of a closed deal on ${row.domain}. ${row.price_band} range, ${row.buyer_type.toLowerCase()} channel${row.days_to_close != null ? `, closed in ${row.days_to_close === 0 ? "one day" : `${row.days_to_close} days`}` : ""}.`}
        type="article"
        siteName="CasperDomains"
        image={`${window.location.origin}/api/og/sold/${slug}`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: `${row.domain} · sold`,
          about: row.domain,
          datePublished: row.sold_at,
          publisher: { "@type": "Organization", name: "CasperDomains" },
        }}
      />
      <SiteHeader />

      <section className="cd-sold-hero" data-testid="sold-detail-hero">
        <div className="cd-container cd-sold-hero__inner">
          <Link to="/#sold" className="cd-link" style={{ color: "rgba(255,255,255,.6)", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
            <ArrowLeft size={14} /> Back to closed deals
          </Link>
          <div style={{ marginTop: 32 }}>
            <div className="cd-sold-hero__eyebrow">
              <span className="cd-sold-hero__eyebrow-dot" aria-hidden />
              Sold{soldDate ? ` · ${soldPretty}` : ""}
            </div>
          </div>
          <h1 className="cd-sold-hero__domain" data-testid="sold-detail-domain">{row.domain}</h1>
          {row.headline && (
            <p style={{ fontSize: 18, color: "rgba(255,255,255,.72)", maxWidth: 620, margin: 0 }}>
              {row.headline}
            </p>
          )}
          <div className="cd-sold-hero__meta">
            <div className="cd-sold-hero__stat" data-testid="sold-price-band">
              <span className="cd-sold-hero__stat-label">Price band</span>
              <span className="cd-sold-hero__stat-value">{row.price_band}</span>
            </div>
            <div className="cd-sold-hero__stat" data-testid="sold-buyer-type">
              <span className="cd-sold-hero__stat-label">Buyer</span>
              <span className="cd-sold-hero__stat-value">{row.buyer_type}</span>
            </div>
            <div className="cd-sold-hero__stat" data-testid="sold-days">
              <span className="cd-sold-hero__stat-label">Days to close</span>
              <span className="cd-sold-hero__stat-value">
                {row.days_to_close == null ? "—" : (row.days_to_close === 0 ? "Same day" : `${row.days_to_close} days`)}
              </span>
            </div>
            <div className="cd-sold-hero__stat">
              <span className="cd-sold-hero__stat-label">TLD</span>
              <span className="cd-sold-hero__stat-value">.{row.tld}</span>
            </div>
          </div>
        </div>
      </section>

      <article className="cd-sold-narrative" data-testid="sold-narrative">
        {renderSections(narrative || fallback)}
        <div style={{ marginTop: 48, padding: "28px 0", borderTop: "1px solid var(--cd-gray-200)", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <Link to="/" className="cd-btn cd-btn--outline">Browse the marketplace</Link>
          <Link to="/about" className="cd-btn cd-btn--primary">Work with me on a domain</Link>
        </div>
      </article>

      <SiteFooter />
      <BullseyeEmbed page="sold" />
    </>
  );
}

/** Generic narrative for domains without an admin-authored story.
 *  Uses only anonymized fields (band + channel + days) so no PII leaks. */
function defaultNarrative(r) {
  const short = (r.domain || "").split(".")[0];
  const daysLine = r.days_to_close == null
    ? "The deal moved without unnecessary back-and-forth."
    : r.days_to_close === 0
      ? "The deal closed inside a single working day."
      : `From first inbound to signed hand-off, the deal closed in ${r.days_to_close} days.`;
  return [
    `## How ${r.domain} moved`,
    `${r.domain} closed in the ${r.price_band} range through a **${r.buyer_type.toLowerCase()}** channel. ${daysLine}`,
    ``,
    `## Why the buyer said yes`,
    `Short, distinct, and phonetically clean. \`${short}\` reads the same across every language the team ships in. There's no dictionary noise to fight, no trademark landmines to defuse, and no legacy backlinks to inherit. The .${r.tld} suffix keeps the price honest while telegraphing intent to anyone who lands on it.`,
    ``,
    `## How the transfer went`,
    `Push completed inside the industry-standard 24-hour window after payment cleared. No mid-flight surprises, no failed authorizations, no support tickets. The new owner had authoritative WHOIS + DNS control before the next business day.`,
    ``,
    `## Working with us`,
    `We source, negotiate, and close domain deals across .com, .io, .ai, and .nl. Every listing on the desk is owned outright, so there's no reserve-price theater, no marketplace fees layered on top. If you're looking for something in the same category as ${r.domain}, [start with the marketplace](/) or [get in touch](/about) and I'll come back with three curated options within a business day.`,
  ].join("\n");
}

/** Split markdown into `## Section` blocks and render each with a
 *  proper `<h2>` + paragraph body. Mirrors the ProjectDetail approach so
 *  admins get a consistent authoring model across surfaces. */
function renderSections(src) {
  const parts = (src || "").replace(/\r\n?/g, "\n").trim().split(/^##\s+(.+)$/m);
  const lede = (parts.shift() || "").trim();
  const sections = lede ? [{ title: null, body: lede }] : [];
  for (let i = 0; i < parts.length; i += 2) {
    sections.push({ title: (parts[i] || "").trim(), body: (parts[i + 1] || "").trim() });
  }
  return sections.map((s, i) => (
    <section key={i} style={{ marginTop: i > 0 ? 32 : 0 }}>
      {s.title && <h2>{s.title}</h2>}
      {renderParagraphs(s.body, `s${i}`)}
    </section>
  ));
}
